import { CacheProvider, LRUCache, NoOpCache } from './cache';
import {
  FusionApplication,
  FusionComponent,
  FusionComponentApi,
  FusionDevHubTemplate,
} from './types/catalog';
import {
  ApiEntity,
  ComponentEntity,
  Entity,
  SystemEntity,
  UserEntity,
} from '@backstage/catalog-model';
import {
  applicationToFinalEntity,
  fusionApiToFinalEntity,
  fusionComponentToFinalEntity,
} from './types/entityMappers';
import {
  FusionGitHubOrgBase,
  FusionQueryProvider,
  FusionUser,
  FusionUserBase,
  fusionUserToEntity,
  paginateOverQuery,
} from '@devxdiscover/devhub-common';
import { paginateOverQueryGenerator } from '../util';

type orgMemberQueryResult = {
  resources: {
    sourceControl: {
      github: {
        organizationsPaged: {
          organizations: {
            name: string;
            id: string;

            membersPaged: {
              members: {
                user: {
                  login: string;
                  identity: FusionUserBase;
                  membership: {
                    organizationsPaged: {
                      organizations: {
                        organization: FusionGitHubOrgBase;
                      }[];
                    };
                  };
                };
              }[];
            };
          }[];
        };
      };
    };
  };
};

type gitHubOrgQueryResult = {
  resources: {
    sourceControl: {
      github: {
        organizationsPaged: {
          organizations: FusionGitHubOrgBase[];
        };
      };
    };
  };
};

type gitHubOrgTeamsQueryResult = {
  resources: {
    sourceControl: {
      github: {
        organizationsPaged: {
          organizations: {
            teamsPaged: {
              teams: {
                id: string;
                name: string;
                membersPaged: {
                  members: {
                    user: {
                      login: string;
                    };
                  }[];
                };
              }[];
            };
          }[];
        };
      };
    };
  };
};

type findUserByLoginResult = {
  identity: {
    user: FusionUserBase & {
      sourceControl: {
        github: {
          userAccounts: {
            membership: {
              organizationsPaged: {
                organizations: {
                  organization: FusionGitHubOrgBase;
                }[];
              };
            };
          }[];
        };
      };
    };
  };
};

type FusionComponentResult = {
  resources: { componentsPaged?: { components: FusionComponent[] } };
};

/**
 * Service that is responsible for fetching data from the Fusion API.
 *
 * This service will be used from the scaffolder backend and catalog API frontend to fetch data from the Fusion API.
 */
export class FusionCatalogService {
  private findApiByIdLoader = this.debounce(this.findApisByIds, 100);
  private findComponentByIdLoader = this.debounce(
    this.findComponentsByIds,
    100,
  );
  private applicationCache: CacheProvider<number, FusionApplication>;
  private componentCache: CacheProvider<string, FusionComponent>;
  private queryProvider: FusionQueryProvider;
  private componentsForUserCache: CacheProvider<string, Set<string>>;
  private apiCache: CacheProvider<string, FusionComponentApi>;
  private appDependenciesCache: CacheProvider<number, FusionApplication[]>;

  constructor(
    queryProvider: FusionQueryProvider,
    cacheType: 'lru' | 'none' = 'lru',
  ) {
    this.queryProvider = queryProvider;
    this.applicationCache =
      cacheType === 'lru' ? new LRUCache({ maxSize: 10 }) : new NoOpCache();
    this.componentCache =
      cacheType === 'lru' ? new LRUCache({ maxSize: 500 }) : new NoOpCache();
    this.componentsForUserCache =
      cacheType === 'lru' ? new LRUCache({ maxSize: 10 }) : new NoOpCache();
    this.apiCache =
      cacheType === 'lru' ? new LRUCache({ maxSize: 500 }) : new NoOpCache();
    this.appDependenciesCache =
      cacheType === 'lru' ? new LRUCache({ maxSize: 500 }) : new NoOpCache();
  }

  /**
   * Finds an application by id
   * @param id - The id of the application
   */
  public async findSystemForApplicationId(
    id: string,
  ): Promise<undefined | (SystemEntity & { relations: Entity['relations'] })> {
    const result = await this.findApplicationById(id);
    if (result) {
      return applicationToFinalEntity(result) as SystemEntity & {
        relations: Entity['relations'];
      };
    }
    return undefined;
  }

  /**
   * Finds an application by ids
   * @param ids - The id of the application
   */
  public async findSystemsForApplicationIds(
    ids: string[],
  ): Promise<
    (undefined | (SystemEntity & { relations: Entity['relations'] }))[]
  > {
    const results = await this.findApplicationsByIds(ids);
    const data = results?.map(
      result =>
        applicationToFinalEntity(result) as SystemEntity & {
          relations: Entity['relations'];
        },
    );
    return data ?? undefined;
  }

  /**
   * Finds a user by id
   * @param id - The id of the user
   */
  public async findUserById(id: string): Promise<undefined | UserEntity> {
    const user = await this.findUserByLogin(id);
    if (!user) {
      return undefined;
    }
    return fusionUserToEntity(user);
  }

  /**
   * Finds an API by id
   * @param apiId - The id of the API
   */
  public async findApiById(apiId: string): Promise<ApiEntity | undefined> {
    const cachedApi = this.apiCache.get(apiId);
    if (cachedApi && cachedApi.full !== undefined) {
      return fusionApiToFinalEntity(cachedApi);
    }
    return await this.findApiByIdLoader(apiId, { fullGraph: true });
  }

  /**
   * Finds a Component by id
   * @param componentId - The id of the component
   */
  public async findComponentById(
    componentId: string,
  ): Promise<
    (ComponentEntity & { relations: Entity['relations'] }) | undefined
  > {
    const cachedComponent = this.componentCache.get(componentId);
    if (cachedComponent && cachedComponent.full !== undefined) {
      return fusionComponentToFinalEntity(cachedComponent);
    }
    return await this.findComponentByIdLoader(componentId, { fullGraph: true });
  }

  /**
   * Finds components by user
   * @param userLogin - The login of the user
   * @param options - The options
   */
  public async findComponentsByUser(
    userLogin: string,
    options?: { fullGraph?: boolean },
  ): Promise<
    ((ComponentEntity & { relations: Entity['relations'] }) | undefined)[]
  > {
    const componentIds = Array.from(
      this.componentsForUserCache.get(userLogin) ?? new Set<string>(),
    );
    const cacheResult = componentIds
      .map(x => this.componentCache.get(x))
      .filter(
        x =>
          x !== undefined &&
          (options?.fullGraph !== true || x.full !== undefined),
      )
      .map(x => fusionComponentToFinalEntity(x!));
    if (cacheResult.length > 0 && cacheResult.length === componentIds.length) {
      return cacheResult;
    }
    const result = (
      await this.queryProvider.query<FusionComponentResult>(
        `
        query ComponentForUser($filter: ResourceCatalogComponentFilter) {
          resources {
            componentsPaged(filter: $filter, pagination: { limit: 1000 }) {
              ${this.getComponentQuery(options?.fullGraph ?? false)}
            }
          }
        }`,
        { filter: { userLogin: userLogin } },
      )
    ).data.resources;
    if (
      result.componentsPaged?.components &&
      result.componentsPaged.components.length > 0
    ) {
      for (const component of result.componentsPaged.components) {
        this.componentCache.set(component.id, component);
        if (!this.componentsForUserCache.get(userLogin)) {
          this.componentsForUserCache.set(userLogin, new Set());
        }
        this.componentsForUserCache.get(userLogin)?.add(component.id);

        for (const api of component.apisConsumedPaged?.componentApis ?? []) {
          this.apiCache.set(api.id, api);
        }
        for (const api of component.apisProvidedPaged?.componentApis ?? []) {
          this.apiCache.set(api.id, api);
        }
      }
      return result.componentsPaged!.components.map(x =>
        x ? fusionComponentToFinalEntity(x) : undefined,
      );
    }
    return [undefined];
  }

  /**
   * Finds all components, paginated
   * @param startDate - The date of the last change for the components that should be included
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findComponentsPaged(
    startDate: Date | null,
    forEachPage: (
      result: FusionComponentResult,
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
    filter?: {
      id?: string;
      name?: string;
      ids?: string[];
    },
  ) {
    await paginateOverQuery<FusionComponentResult>(
      `
        query AllComponents($pagination: PaginationInput,$filter: ResourceCatalogComponentFilter) {
          resources {
            componentsPaged(pagination: $pagination, filter: $filter) {
              ${this.getComponentQuery(true)}
            }
          }
        }`,
      {
        filter: {
          lastChangedAt: {
            after: startDate,
          },
          ...filter,
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: result =>
          (result?.resources?.componentsPaged?.components?.length ?? 0) > 0,
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds all components, paginated with a generator
   * @param params - The parameters
   */
  public async *findComponentsPagedGenerator({
    startDate = null,
    pageSize = 1000,
    includeSearchIndex = false,
    filter = {},
  }: {
    startDate?: Date | null;
    pageSize?: number;
    includeSearchIndex?: boolean;
    filter?: Record<string, any>;
  }): AsyncGenerator<FusionComponentResult> {
    const result: AsyncGenerator<FusionComponentResult> =
      paginateOverQueryGenerator<FusionComponentResult>(
        `
        query AllComponents($pagination: PaginationInput,$filter: ResourceCatalogComponentFilter) {
          resources {
            componentsPaged(pagination: $pagination, filter: $filter) {
              ${this.getComponentQuery(true, includeSearchIndex)}
            }
          }
        }`,
        {
          filter: {
            lastChangedAt: {
              after: startDate,
            },
            ...filter,
          },
        },
        {
          queryProvider: this.queryProvider,
          stepSize: pageSize,
          haveMore: currentPage =>
            (currentPage?.resources?.componentsPaged?.components?.length ?? 0) >
            0,
        },
      );
    for await (const record of result) {
      yield record;
    }
  }

  /**
   * Finds all component APIs, paginated
   * @param startDate - The date of the last change for the components that should be included
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findComponentApisPaged(
    startDate: Date | null,
    forEachPage: (
      result: {
        resources: {
          componentApisPaged?: { componentApis: FusionComponentApi[] };
        };
      },
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
    filter?: {
      id?: string;
      name?: string;
      ids?: string[];
      userLogin?: string;
    },
  ) {
    await paginateOverQuery<{
      resources: {
        componentApisPaged?: { componentApis: FusionComponentApi[] };
      };
    }>(
      `
        query AllComponentApis($pagination: PaginationInput,$filter: ResourceCatalogComponentApiFilter) {
          resources {
            componentApisPaged(pagination: $pagination, filter: $filter) {
              ${this.getApiQuery(true)}
            }
          }
        }`,
      {
        filter: {
          lastChangedAt: {
            after: startDate,
          },
          ...filter,
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: result =>
          (result?.resources?.componentApisPaged?.componentApis?.length ?? 0) >
          0,
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds all DevHub Templates, paginated
   * @param startDate - The date of the last change for the templates that should be included
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findDevHubTemplatesPaged(
    startDate: Date | null,
    forEachPage: (
      result: {
        resources: {
          devHubTemplatesPaged?: { templates: FusionDevHubTemplate[] };
        };
      },
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
  ) {
    await paginateOverQuery<{
      resources: {
        devHubTemplatesPaged?: { templates: FusionDevHubTemplate[] };
      };
    }>(
      `
      query FindAllTemplates($filter: ResourceCatalogDevHubTemplateFilter, $pagination: PaginationInput) {
        resources {
          devHubTemplatesPaged(filter: $filter, pagination: $pagination) {
            templates {
              full:id
              id
              name
              title
              type
              description
              lifecycle
              techDocsRef
              sourceLocationUrl
              scmSource
              scmSlug
              tags
              links {
                icon
                title
                type
                url
              }
              annotations
              lastChangedAt
              steps
              parameters
              output
              
            }
          }
        }
      }`,
      {
        filter: {
          lastChangedAt: {
            after: startDate,
          },
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: result =>
          (result?.resources?.devHubTemplatesPaged?.templates?.length ?? 0) > 0,
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds all GitHub organizations, paginated
   * @param startDate - The date of the last change for the organizations that should be included
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findGitHubOrganizationsPaged(
    startDate: Date | null,
    forEachPage: (
      result: gitHubOrgQueryResult,
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
    filter?: {
      name?: string;
    },
  ) {
    await paginateOverQuery<gitHubOrgQueryResult>(
      `query getGroupsForIdentityImport($pagination: PaginationInput, $filter: ResourceCatalogGitHubOrganizationFilter) {
          resources {
            sourceControl {
              github {
                organizationsPaged(pagination: $pagination, filter: $filter) {
                  organizations {
                    full:id
                    name
                    id                        
                  }
                }
              }
            }
          }
        }`,
      {
        filter: {
          lastChangedAt: {
            after: startDate,
          },
          ...filter,
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: result => {
          const organizations =
            result?.resources?.sourceControl?.github.organizationsPaged
              .organizations;
          return organizations && organizations.length > 0;
        },
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds all GitHub teams for the given organizationId, paginated
   * @param organizationId - The id of the organization
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findGitHubTeamsPaged(
    organizationId: string,
    forEachPage: (
      result: gitHubOrgTeamsQueryResult,
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
  ) {
    await paginateOverQuery<gitHubOrgTeamsQueryResult>(
      `query ComponentsPaged( $organizationsPagedFilter: ResourceCatalogGitHubOrganizationFilter) {
        resources {
          sourceControl {
            github {
              organizationsPaged(filter: $organizationsPagedFilter, pagination: {limit:1}) {
                organizations {
                  name
                  teamsPaged(pagination: {limit:1000}) {
                    teams {
                      id
                      name
                      membersPaged(pagination: {limit:1000})  {
                        members {
                          user {
                            id
                            login
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`,
      {
        organizationsPagedFilter: {
          id: organizationId,
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: () => {
          // We're only getting one page since it should have everything in it (unless there's an org with more than 1000 teams
          // or a team with more than 1000 users.
          return false;
        },
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds all GitHub organization members, paginated
   * @param startDate - The date of the last change for the organization members that should be included
   * @param orgName - The name of the organization
   * @param includeIdentity - Whether to include the identity
   * @param includeMembership - Whether to include the membership
   * @param forEachPage - The function to call for each page
   * @param pageSize - The page size
   */
  public async findGitHubOrganizationMembersPaged(
    startDate: Date | null,
    orgName: string,
    includeIdentity: boolean,
    includeMembership: boolean,
    forEachPage: (
      result: orgMemberQueryResult,
      offset?: number,
    ) => Promise<void>,
    pageSize: number = 1000,
  ) {
    let identityBlock = `identity { firstName lastName displayName email department login title userId }`;
    if (!includeIdentity) {
      identityBlock = '';
    }
    let membershipBlock = `membership { organizationsPaged(pagination:{limit:1000}) { organizations { organization { id name } } } }`;
    if (!includeMembership) {
      membershipBlock = '';
    }
    await paginateOverQuery<orgMemberQueryResult>(
      `query getUsersForGroup($pagination: PaginationInput, $filter: ResourceCatalogGitHubOrganizationFilter) {
                resources {
                    sourceControl {
                      github {
                        organizationsPaged(filter: $filter) {
                          organizations {
                            full:id
                            name
                            id
                            membersPaged(pagination: $pagination) {
                              members {
                                user {
                                  login
                                  ${identityBlock}
                                  ${membershipBlock}
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
            }`,
      {
        filter: {
          name: orgName,
          lastChangedAt: {
            after: startDate,
          },
        },
      },
      {
        queryProvider: this.queryProvider,
        stepSize: pageSize,
        haveMore: result =>
          result?.resources?.sourceControl?.github.organizationsPaged
            .organizations &&
          result.resources.sourceControl?.github.organizationsPaged
            .organizations.length > 0 &&
          (result.resources.sourceControl?.github.organizationsPaged
            .organizations[0].membersPaged?.members?.length ?? 0) > 0,
        forEachPage: forEachPage,
        onFailed: err => {
          throw err;
        },
      },
    );
  }

  /**
   * Finds components by ids
   * @param componentIds - The id of the components
   * @param options - The options
   */
  public async findComponentsByIds(
    componentIds: string[],
    options?: { fullGraph?: boolean },
  ): Promise<
    ((ComponentEntity & { relations: Entity['relations'] }) | undefined)[]
  > {
    const cacheResult = componentIds
      .map(x => this.componentCache.get(x))
      .filter(
        x =>
          x !== undefined &&
          (options?.fullGraph !== true || x.full !== undefined),
      )
      .map(x => fusionComponentToFinalEntity(x!));
    if (cacheResult.length === componentIds.length) {
      return cacheResult;
    }
    const result = (
      await this.queryProvider.query<FusionComponentResult>(
        `
        query ComponentById($filter: ResourceCatalogComponentFilter) {
          resources {
            componentsPaged(filter: $filter) {
              ${this.getComponentQuery(options?.fullGraph ?? false)}
            }
          }
        }`,
        { filter: { ids: componentIds } },
      )
    ).data.resources;
    if (
      result.componentsPaged?.components &&
      result.componentsPaged.components.length > 0
    ) {
      for (const component of result.componentsPaged.components) {
        this.componentCache.set(component.id, component);
        for (const api of component.apisConsumedPaged?.componentApis ?? []) {
          this.apiCache.set(api.id, api);
        }
        for (const api of component.apisProvidedPaged?.componentApis ?? []) {
          this.apiCache.set(api.id, api);
        }
      }
      return componentIds
        .map(x => result.componentsPaged!.components.find(y => y.id === x))
        .map(x => (x ? fusionComponentToFinalEntity(x) : undefined));
    }
    return componentIds.map(() => undefined);
  }

  /**
   * Finds APIs by ids
   * @param apiIds - The ids of the APIs
   * @param options - The options
   */
  public async findApisByIds(
    apiIds: string[],
    options?: { fullGraph?: boolean },
  ): Promise<(ApiEntity | undefined)[]> {
    const cacheResult = apiIds
      .map(x => this.apiCache.get(x))
      .filter(
        x =>
          x !== undefined &&
          (options?.fullGraph !== true || x.full !== undefined),
      )
      .map(x => fusionApiToFinalEntity(x!));
    if (cacheResult.length === apiIds.length) {
      return cacheResult;
    }
    const result = (
      await this.queryProvider.query<{
        resources: {
          componentApisPaged?: { componentApis: FusionComponentApi[] };
        };
      }>(
        `
        query ApiById($filter: ResourceCatalogComponentApiFilter) {
          resources {
            componentApisPaged(filter: $filter) {
              ${this.getApiQuery(options?.fullGraph ?? false)}
            }
          }
        }`,
        { filter: { ids: apiIds } },
      )
    ).data.resources;
    if (
      result.componentApisPaged?.componentApis &&
      result.componentApisPaged.componentApis.length > 0
    ) {
      for (const api of result.componentApisPaged.componentApis) {
        this.apiCache.set(api.id, api);

        // Cache the components
        if (api.consumedByComponentsPaged) {
          for (const component of api.consumedByComponentsPaged.components) {
            this.componentCache.set(component.id, component);
          }
        }
        if (api.providedByComponentsPaged) {
          for (const component of api.providedByComponentsPaged.components) {
            this.componentCache.set(component.id, component);
          }
        }
      }
      const apis = result.componentApisPaged.componentApis;
      return apiIds
        .map(x => apis.find(y => y.id === x))
        .map(x => (x ? fusionApiToFinalEntity(x) : undefined));
    }
    return apiIds.map(() => undefined);
  }

  /**
   * Finds users by login
   * @param login The login
   * @private
   */
  public async findUserByLogin(login: string): Promise<FusionUser | undefined> {
    const result = (
      await this.queryProvider.query<findUserByLoginResult>(
        `
        query findUserByLogin($login: String) {
          identity {
            user(login: $login) {
              full:login
              department
              displayName
              email
              firstName
              lastName
              login
              userId
              title
              sourceControl {
                github {
                  userAccounts {
                    membership {
                      organizationsPaged(pagination: { limit: 1000 }) {
                        organizations {
                          organization {
                            full: 
                            id
                            name
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `,
        { login },
      )
    ).data.identity;
    if (result.user) {
      return {
        department: result.user.department,
        displayName: result.user.displayName,
        email: result.user.email,
        firstName: result.user.firstName,
        lastName: result.user.lastName,
        login: result.user.login,
        userId: result.user.userId,
        title: result.user.title,
        memberOf: {
          organization: result.user?.sourceControl?.github?.userAccounts
            .flatMap(x => x.membership?.organizationsPaged?.organizations)
            .map(x => x.organization),
        },
      };
    }
    return undefined;
  }

  /**
   * Find applications by ids
   * @param ids - The applicationIds
   */
  private async findApplicationsByIds(
    ids: string[],
  ): Promise<FusionApplication[]> {
    const dependencyQuery = `              
              dependsOnPaged(pagination: {limit: 100}, filter: { applicationStatuses: [Active, PreProd] }) {
                 dependencies {
                    sources { name }
                    application {
                       applicationId
                        name
                    }
                  }
                }
              dependencyOfPaged(pagination: {limit: 100}, filter: { applicationStatuses: [Active, PreProd] }) {
                 dependencies {
                    sources { name }
                    application {
                       applicationId
                        name
                    }
                  }
                }`;
    const result = (
      await this.queryProvider.query<{
        resources: { applicationsPaged: { applications: FusionApplication[] } };
      }>(
        `
        query findApplicationsByIds($filter: ResourceCatalogApplicationFilter!) {
          resources {
            applicationsPaged(filter: $filter) {
              applications {
                full:applicationId
                applicationId
                name
                componentsPaged(pagination: { limit: 1000 }) {
                  ${this.getComponentQuery(true)}
                }
                componentApisPaged(pagination: { limit: 1000 }) {
                  ${this.getApiQuery(true)}
                }
                ${dependencyQuery}
              }
            }
          }
        }`,
        {
          filter: {
            applicationIds: ids.map(x => parseInt(x, 10)),
            applicationStatuses: ['Active', 'PreProd'],
          },
        },
      )
    ).data.resources;
    if (result.applicationsPaged?.applications) {
      const applications = result.applicationsPaged.applications;
      applications.forEach(app => {
        const application = JSON.parse(JSON.stringify(app));
        this.applicationCache.set(application.applicationId, app);
      });

      return applications;
    }
    return [];
  }

  /**
   * Finds an application by id
   * @param id - The id of the application
   */
  private async findApplicationById(
    id: string,
  ): Promise<FusionApplication | undefined> {
    if (!id) {
      return undefined;
    }
    let applicationId = 0;
    try {
      applicationId = parseInt(id, 10);
    } catch {
      return undefined;
    }
    const cachedApplication = this.applicationCache.get(applicationId);
    if (cachedApplication) {
      return cachedApplication;
    }
    const dependencyQuery = `              
              dependsOnPaged(pagination: {limit: 100}, filter: { applicationStatuses: [Active, PreProd] }) {
                 dependencies {
                    sources { name }
                    application {
                       applicationId
                        name
                    }
                  }
                }
              dependencyOfPaged(pagination: {limit: 100}, filter: { applicationStatuses: [Active, PreProd] }) {
                 dependencies {
                    sources { name }
                    application {
                       applicationId
                        name
                    }
                  }
                }`;
    const result = (
      await this.queryProvider.query<{
        resources: { application?: FusionApplication };
      }>(
        `
        query findApplicationById($applicationId: Int!) {
          resources {
            application(applicationId: $applicationId) {
              full:applicationId
              applicationId
              name
              componentsPaged(pagination: { limit: 100 }) {
                ${this.getComponentQuery(true)}
              }
              componentApisPaged(pagination: { limit: 1000 }) {
                ${this.getApiQuery(true)}
              }
              ${dependencyQuery}
            }
          }
        }`,
        { applicationId },
      )
    ).data.resources;
    if (result.application) {
      // We need to clone the object because Apollo marks it as non-extensible
      const application = JSON.parse(
        JSON.stringify(result.application),
      ) as FusionApplication;

      // Add the components to the cache
      for (const component of application?.componentsPaged?.components ?? []) {
        this.componentCache.set(component.id, component);
      }

      // Cache the APIs
      for (const api of application?.componentApisPaged?.componentApis ?? []) {
        if (!api.applicationsPaged) {
          api.applicationsPaged = { applications: [] };
        }
        api.applicationsPaged.applications = [
          {
            applicationId: application.applicationId,
            name: application.name,
          },
        ];
        this.apiCache.set(api.id, api);
      }

      application.dependsOnPaged?.dependencies.forEach(dependency => {
        const dependentApplication =
          dependency.application as FusionApplication;
        if (dependentApplication) {
          if (this.appDependenciesCache.get(applicationId)) {
            const existing = this.appDependenciesCache.get(
              Number(application.applicationId),
            );
            if (existing) {
              existing.push(dependentApplication);
              this.appDependenciesCache.set(applicationId, existing);
            }
          } else {
            this.appDependenciesCache.set(applicationId, [
              dependentApplication,
            ]);
          }
        }
      });
      return application;
    }
    return undefined;
  }

  /**
   * Returns the query for a component
   * @param fullGraph - Whether to include the full graph
   * @param includeSearchIndex - Whether to include the search index
   * @private
   */
  private getComponentQuery(
    fullGraph: boolean = false,
    includeSearchIndex: boolean = false,
  ): string {
    let query = `components {
              id
              lifecycle
              name
              sourceLocationUrl
              title
              type
              description
              tags
              annotations
              devHubOwner
              techDocsRef
              ${includeSearchIndex ? 'techDocsSearchIndex' : ''}
              links {
                url
                title
                icon
                type
              }
              scmSlug `;
    if (fullGraph) {
      query += `
              full:id
              applicationsPaged {
               ${this.getApplicationQuery()}
              }
              apisConsumedPaged(pagination: { limit: 1000 }) {
                ${this.getApiQuery()}
              }
              apisProvidedPaged(pagination: { limit: 1000 }) {
                ${this.getApiQuery()}
              }
              dependencyOfPaged(pagination: { limit: 1000 }) {
                  components {
                    id
                  }
              }
              dependsOnPaged(pagination: { limit: 1000 }) {
                  components {
                    id
                  }
              }
              `;
    }
    query += `}`;
    return query;
  }

  /**
   * Returns the query for an API
   * @param fullGraph - Whether to include the full graph
   * @private
   */
  private getApiQuery(fullGraph: boolean = false): string {
    let query = `componentApis {
              id
              lifecycle
              name
              title
              type
              description
              sourceLocationUrl
              annotations
              devHubOwner
              links {
                url
                title
                icon
                type
              }
              tags
              scmSlug `;
    if (fullGraph) {
      query += `
              full:id
              definitionPacked
              applicationsPaged {
               ${this.getApplicationQuery()}
              }
              providedByComponentsPaged(pagination: { limit: 1000 }) {
                ${this.getComponentQuery()}
              }
              consumedByComponentsPaged(pagination: { limit: 1000 }) {
                ${this.getComponentQuery()}
              }`;
    }
    query += `}`;
    return query;
  }

  /**
   * Returns the query for an application
   * @private
   */
  private getApplicationQuery() {
    return `applications {
              applicationId
              name
            }`;
  }

  /**
   * Collects multiple calls to the same function and debounces them
   * @param func The function to call
   * @param delay The delay
   * @private
   */
  private debounce<T>(
    func: (ids: string[], params?: Record<string, any>) => Promise<T[]>,
    delay: number,
  ): (id: string, params?: Record<string, any>) => Promise<T> {
    let timeoutId: NodeJS.Timeout;
    const resolveMap: { id: string; resolve: (value: T) => void }[] = [];
    let collectingIds: string[] = [];
    const self = this;
    const debounced = (id: string, params?: Record<string, any>) => {
      collectingIds.push(id);

      const promise = new Promise<T>(resolve => {
        resolveMap.push({ id, resolve });
      });

      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const ids = collectingIds;
        collectingIds = [];
        const results = await func.bind(self)(ids, params);
        ids.forEach((currentId, index) => {
          const entry = resolveMap.find(x => x.id === currentId);

          if (entry) {
            resolveMap.splice(resolveMap.indexOf(entry), 1);
            entry.resolve(results[index]);
          }
        });
      }, delay);

      return promise;
    };

    return debounced;
  }
}
